import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, CheckCircle, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface SelectDocumentStepProps {
  taskId: string;
  selectedDocumentId: string;
  onSelect: (id: string) => void;
}

export default function SelectDocumentStep({
  taskId,
  selectedDocumentId,
  onSelect,
}: SelectDocumentStepProps) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, [taskId]);

  const loadDocuments = async () => {
    try {
      // Get all finalized documents for this task
      const { data: allDocs } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', taskId)
        .eq('status', 'final')
        .order('generated_at', { ascending: false });

      if (!allDocs || allDocs.length === 0) {
        setDocuments([]);
        return;
      }

      // Get all completed signature requests for these documents
      const docIds = allDocs.map(doc => doc.id);
      const { data: completedRequests } = await supabase
        .from('signature_requests')
        .select('document_id')
        .in('document_id', docIds)
        .eq('status', 'completed');

      // Filter out documents that have completed signature requests
      const completedDocIds = new Set(
        completedRequests?.map(req => req.document_id) || []
      );
      
      const availableDocs = allDocs.filter(
        doc => !completedDocIds.has(doc.id)
      );

      setDocuments(availableDocs);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading documents...</div>;
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-8">
        <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">
          No finalized documents available for signatures.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold mb-2">Select Document</h3>
        <p className="text-sm text-muted-foreground">
          Choose the document that needs signatures
        </p>
      </div>

      <div className="space-y-3">
        {documents.map((doc) => (
          <Card
            key={doc.id}
            className={`p-4 cursor-pointer transition-all ${
              selectedDocumentId === doc.id
                ? 'border-primary ring-2 ring-primary'
                : 'hover:border-primary/50'
            }`}
            onClick={() => onSelect(doc.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <FileText className="w-5 h-5 mt-0.5 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium">{doc.document_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {doc.document_type}
                  </div>
                  {doc.requires_signatures && (
                    <Badge variant="secondary" className="mt-2">
                      Requires Signatures
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                {doc.file_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(doc.file_url, '_blank');
                    }}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
                {selectedDocumentId === doc.id && (
                  <CheckCircle className="w-5 h-5 text-primary" />
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
